/**
 * API エラーの正規化。
 *
 * SpiffArena backend はエラー時に `{ error_code, message }` を返す
 * （error_code は i18n キーとして使える安定値）。パースできない応答も
 * 同じ形に正規化し、呼び出し側が status / errorCode だけを見れば済むようにする。
 */

export class ApiError extends Error {
  readonly status: number;
  /** SpiffArena の error_code。無ければ `http_<status>`。 */
  readonly errorCode: string;
  /** パース済み body（パースできなければ生文字列）。 */
  readonly body: unknown;

  constructor(
    status: number,
    errorCode: string,
    message: string,
    body: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errorCode = errorCode;
    this.body = body;
  }
}

/** 401。セッションが無い／期限切れ。 */
export class UnauthorizedError extends ApiError {
  constructor(
    status: number,
    errorCode: string,
    message: string,
    body: unknown,
  ) {
    super(status, errorCode, message, body);
    this.name = "UnauthorizedError";
  }
}

/** 403。認証済みだが権限がない。 */
export class ForbiddenError extends ApiError {
  constructor(
    status: number,
    errorCode: string,
    message: string,
    body: unknown,
  ) {
    super(status, errorCode, message, body);
    this.name = "ForbiddenError";
  }
}

/** fetch 自体が失敗した（オフライン・DNS・CORS など）。HTTP ステータスを持たない。 */
export class NetworkError extends Error {
  override readonly cause: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "NetworkError";
    this.cause = cause;
  }
}

export function normalizeApiError(
  status: number,
  statusText: string,
  bodyText: string,
): ApiError {
  const parsed = tryParseJson(bodyText);
  const fields = extractErrorFields(parsed);

  const errorCode = fields.errorCode ?? `http_${status}`;
  const message = fields.message ?? `${status} ${statusText}`.trim();
  const body = parsed ?? bodyText;

  if (status === 401)
    return new UnauthorizedError(status, errorCode, message, body);
  if (status === 403)
    return new ForbiddenError(status, errorCode, message, body);
  return new ApiError(status, errorCode, message, body);
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

function tryParseJson(text: string): unknown {
  if (text === "") return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function extractErrorFields(parsed: unknown): {
  errorCode: string | null;
  message: string | null;
} {
  if (parsed === null || typeof parsed !== "object") {
    return { errorCode: null, message: null };
  }

  const record = parsed as Record<string, unknown>;
  return {
    errorCode: typeof record.error_code === "string" ? record.error_code : null,
    message: typeof record.message === "string" ? record.message : null,
  };
}
